import {
  CLAIM_VERDICT_LABEL,
  GRADE_TONE,
  type EvidenceGrade,
  changePartLabel,
  claimFails,
  INDUSTRIES,
  planMissions,
  THREATS,
  type CyberScoreResult,
  type ScoreTrace,
  type Threat,
} from '@cyberscore/contracts';
import {
  ChevronRight,
  FileCheck2,
  Info,
  ShieldAlert,
  ShieldCheck,
  Target,
  TriangleAlert,
} from 'lucide-react';
import { href } from '../../lib/router';
import { useAssessment } from '../assessments/api';
import { OutsideIn } from './OutsideIn';
import { MemoCard } from '../assistant/MemoCard';
import { useSession, can } from '../../lib/session';
import { useScore, useScoreChange, useSeesPortfolio, type StoredScoreRow } from './api';
import { verdictPill } from '../claims/ClaimList';
import { bandLabel, bandTone, pct, rm, signed } from './format';

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-MY', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * One assessment's CyberScore, read-only (task 29): how it was reached, what caps it, what would lift it,
 * the loss model for staff, and what moved it. Everything comes from the score's own explanation.
 */
export function ScorePage({
  id,
  embedded = false,
}: {
  id: string;
  /** Inside Client 360's Score tab: no crumbs or heading. */ embedded?: boolean;
}) {
  const detail = useAssessment(id);
  const view = useScore(id);
  const staff = useSeesPortfolio();
  const { tenant } = useSession();
  const drafts = can(tenant, 'assistant.memo');

  if (detail.isPending || view.isPending) return <div className="empty">Loading…</div>;
  if (detail.isError || view.isError)
    return (
      <div className="empty">
        <strong>Could not load the score</strong>
        {(detail.error ?? view.error)?.message}
      </div>
    );
  const a = detail.data;
  const { score: r, explanation: trace, history, stored, grade } = view.data;

  return (
    <>
      {!embedded && (<>
      <div className="crumbs">
        {staff ? <a href={href.portfolio()}>Portfolio</a> : <a href={href.assessments()}>Assessments</a>}
        <ChevronRight size={14} />
        <a href={href.assessment(id)}>{a.customer_name}</a>
        <ChevronRight size={14} />
        CyberScore
      </div>
      <div className="page-head">
        <h1>{a.customer_name}</h1>
        <p>
          {a.title} · CyberScore model <span className="mono">{r.algorithm}</span>
        </p>
      </div>
      </>)}

      <Snapshot r={r} trace={trace} history={history} grade={grade} />
      <BelowMinimum r={r} />
      {r.provisional && (
        <div className="note warn mt">
          <TriangleAlert size={16} />
          <span>
            Provisional: {r.controls.applicable - r.controls.answered} of {r.controls.applicable} applicable controls
            have no answer yet, so the score can still move a lot.
          </span>
        </div>
      )}
      {!stored && (
        <div className="note mt">
          <Info size={16} />
          <span>Something changed since the last stored score (for example a date passed), so this is computed now.</span>
        </div>
      )}

      <div className="layout-side mt">
        <div className="stack">
          <Exceptions r={r} trace={trace} />
          <Steps trace={trace} />
          <Domains r={r} />
          <Checked trace={trace} />
          {r.loss && <Loss loss={r.loss} />}
        </div>
        <aside className="stack">
          <Missions trace={trace} />
          <Resilience r={r} />
          {drafts && <MemoCard id={id} business={a.customer_name} />}
          {staff && <OutsideIn id={id} />}
          <History id={id} history={history} />
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

/** The score is below the lowest band its industry profile accepts (task 79): refer it. */
export function BelowMinimum({ r }: { r: Pick<CyberScoreResult, 'profile'> }) {
  if (!r.profile?.below_minimum || !r.profile.minimum_band) return null;
  return (
    <div className="note bad mt" role="status">
      <ShieldAlert size={16} />
      <span>
        Below the minimum for {r.profile.name.toLowerCase()}: {r.profile.name} needs{' '}
        {bandLabel(r.profile.minimum_band)} or better, so this is referred before cover is offered.
      </span>
    </div>
  );
}

function Snapshot({
  r,
  trace,
  history,
  grade,
}: {
  r: CyberScoreResult;
  trace: ScoreTrace;
  history: StoredScoreRow[];
  grade: EvidenceGrade;
}) {
  const before = history[1];
  const residual = trace.steps.find((s) => s.key === 'residual')?.value ?? r.residual_risk;
  return (
    <section className="card snapshot">
      <div>
        <div className="eyebrow">CyberScore</div>
        <div className="big-score">
          {r.score}
          <span className="muted"> / 1000</span>
        </div>
        <span className={`pill ${bandTone(r.band)}`}>{bandLabel(r.band)}</span>
        {before && (
          <span className="muted small">
            {' '}
            {history[0]!.score === before.score ? 'no change' : signed(history[0]!.score - before.score)} since the score before
          </span>
        )}
      </div>
      <div>
        <div className="eyebrow">Evidence grade</div>
        <div className={`big-num grade-big ${GRADE_TONE[grade.grade]}-t`} aria-label={`Evidence grade ${grade.grade}`}>{grade.grade}</div>
        <ul className="muted small grade-reasons">
          {grade.reasons.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </div>
      <div>
        <div className="eyebrow">Kill-switch cap</div>
        <div className={`big-num${r.kill_switches.cap ? ' bad-t' : ''}`}>{r.kill_switches.cap ?? 'None'}</div>
        <div className="muted small">
          {r.kill_switches.cap === null
            ? `${r.kill_switches.pending.length} kill switches still open`
            : r.uncapped > r.kill_switches.cap
              ? `${r.uncapped} before the cap`
              : 'Not limiting yet: the score is below it'}
        </div>
      </div>
      <div>
        <div className="eyebrow">Residual risk</div>
        <div className="big-num">{Math.round(residual)}</div>
        <div className="muted small">of {Math.round(r.inherent.value)} inherent, after controls</div>
      </div>
      {r.loss && (
        <div>
          <div className="eyebrow">Expected annual loss</div>
          <div className="big-num">{rm(r.loss.expected_annual)}</div>
          <div className="muted small">
            {rm(r.loss.expected_annual_low)}–{rm(r.loss.expected_annual_high)}, illustrative
          </div>
        </div>
      )}
    </section>
  ); // prettier-ignore
}

/**
 * Controls whose answers were checked against the business's documents (task 62): a supported answer
 * counts as read from a document; one the documents don't back keeps its self-declared confidence.
 */
function Checked({ trace }: { trace: ScoreTrace }) {
  const checked = trace.controls.filter((c) => c.claims);
  if (!checked.length) return null;
  const failing = checked.filter((c) => Object.values(c.claims!).some(claimFails)).length;
  return (
    <section className="card pad" aria-labelledby="checked-h">
      <h2 id="checked-h" className="section-title row gap">
        <FileCheck2 size={17} /> Answers checked against documents
      </h2>
      <p className="muted small">
        {checked.filter((c) => c.checked).length} of {checked.length} checked controls are backed by a
        document and count at its confidence
        {failing ? `; ${failing} ${failing === 1 ? 'isn’t' : 'aren’t'} backed` : ''}.
      </p>
      <ul className="claim-list checked-list">
        {checked.map((c) => (
          <li key={c.control} className="row-between gap">
            <span className="small">
              <span className="mono">{c.control}</span> {c.name}
            </span>
            <span className="row gap">
              {Object.entries(c.claims!).map(([q, v]) => (
                <span key={q} className={`pill ${verdictPill(v)}`} title={q}>
                  {CLAIM_VERDICT_LABEL[v]}
                </span>
              ))}
              <span className="muted small num">{pct(c.confidence)}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Exceptions({ r, trace }: { r: CyberScoreResult; trace: ScoreTrace }) {
  const name = new Map(trace.controls.map((c) => [c.control, c.name]));
  const failed = [...r.kill_switches.failed].sort((a, b) => a.cap - b.cap);
  return (
    <section className="card pad">
      <div className="card-title">
        <ShieldAlert size={16} /> Underwriting exceptions
        <span className={`pill ${failed.length ? 'bad' : 'ok'}`}>{failed.length ? `${failed.length} open` : 'None'}</span>
      </div>
      <p className="muted small">Kill switches: critical controls that cap the score whatever the average.</p>
      {failed.length ? (
        <ol className="exc-list">
          {failed.map((k) => (
            <li key={k.control} className={k.severity === 'medium' ? 'warn' : ''}>
              <b>{k.name}</b>
              <span className="muted small">
                {k.control} · {k.severity} · caps the score at {k.cap}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="ok-line">
          <ShieldCheck size={15} /> Every kill switch answered so far is in place.
        </p>
      )}
      {r.kill_switches.pending.length > 0 && (
        <p className="muted small mt">
          Not answered yet, or “not sure”: {r.kill_switches.pending.map((c) => `${name.get(c) ?? c} (${c})`).join(', ')}.
        </p>
      )}
    </section>
  ); // prettier-ignore
}

function Steps({ trace }: { trace: ScoreTrace }) {
  return (
    <section className="card pad">
      <div className="card-title">How the score was reached</div>
      <div className="table-wrap">
        <table className="grid steps">
          <tbody>
            {trace.steps.map((s) => (
              <tr key={s.key} className={s.key === 'score' ? 'total' : undefined}>
                <td>
                  {s.label}
                  <div className="mono small muted formula">{s.expression}</div>
                </td>
                <td className="right num">{Number.isInteger(s.value) ? s.value : s.value.toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Domains({ r }: { r: CyberScoreResult }) {
  const domains = [...r.domains].sort((a, b) => b.weight - a.weight);
  return (
    <section className="card pad">
      <div className="card-title">Control domains</div>
      <p className="muted small">Declared, and after evidence confidence (verified). A wide gap means mostly self-declared answers.</p>
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Domain</th>
              <th className="right">Weight</th>
              <th className="right">Declared</th>
              <th className="right">Verified</th>
              <th className="right">Answered</th>
            </tr>
          </thead>
          <tbody>
            {domains.map((d) => (
              <tr key={d.domain}>
                <td>
                  <span className="mono small muted">{d.domain}</span> {d.name}
                </td>
                <td className="right num">{pct(d.weight)}</td>
                <td className="right num">{Math.round(d.score)}</td>
                <td className={`right num${d.score - d.effective >= 15 ? ' warn-t' : ''}`}>{Math.round(d.effective)}</td>
                <td className="right num muted">
                  {d.answered}/{d.controls}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  ); // prettier-ignore
}

function Loss({ loss }: { loss: NonNullable<CyberScoreResult['loss']> }) {
  return (
    <section className="card pad">
      <div className="card-title">Loss model</div>
      <p className="muted small">
        Illustrative, per year. Ranges widen when evidence is weak. For discussing limits and retentions; not a price.
      </p>
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Scenario</th>
              <th className="right">Chance a year</th>
              <th className="right">Cost of one event</th>
              <th className="right">Expected</th>
            </tr>
          </thead>
          <tbody>
            {loss.scenarios.map((s) => (
              <tr key={s.threat}>
                <td>{s.name}</td>
                <td className="right num">{(s.probability * 100).toFixed(1)}%</td>
                <td className="right num">
                  {rm(s.low)}–{rm(s.high)}
                </td>
                <td className="right num">{rm(s.expected)}</td>
              </tr>
            ))}
            <tr className="total">
              <td>Expected annual loss</td>
              <td />
              <td className="right num muted">
                {rm(loss.expected_annual_low)}–{rm(loss.expected_annual_high)}
              </td>
              <td className="right num">{rm(loss.expected_annual)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="small mt">
        Probable maximum loss (1 in 100): <b>{rm(loss.probable_maximum)}</b>
      </p>
      {loss.assumptions.length > 0 && (
        <ul className="muted small assumptions">
          {loss.assumptions.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
    </section>
  ); // prettier-ignore
}

/**
 * Missions (decision 0030): the controls behind one plain goal, scored together. One control moves the
 * score by a few points; a mission's next steps and the whole mission show what doing them together adds.
 */
function Missions({ trace }: { trace: ScoreTrace }) {
  const plans = planMissions(trace);
  // A mission is done when nothing in it would add points, even once a failed kill switch is fixed.
  const open = plans.filter((m) => m.next.steps.length > 0);
  const done = plans.filter((m) => m.next.steps.length === 0);
  return (
    <section className="card pad">
      <div className="card-title">
        <Target size={16} /> Missions
      </div>
      <p className="muted small">Points for doing each mission's controls together, fully in place. Missions don't add up: each counts from today's score.</p>
      {open.length ? (
        <ol className="missions">
          {open.map((m, i) => (
            <li key={m.id}>
              <details open={i === 0}>
                <summary>
                  <span className="grow">
                    <b>{m.name}</b>
                    {m.pack && <span className="badge">{INDUSTRIES[m.pack]}</span>}
                    {m.lifts_cap && <span className="badge bad">Lifts the cap</span>}
                    <span className="mission-progress">
                      <span className="meter thin">
                        <span className={`fill ${m.in_place / m.controls >= 0.75 ? 'ok' : m.in_place / m.controls < 0.45 ? 'warn' : ''}`} style={{ width: `${(100 * m.in_place) / m.controls}%` }} />
                      </span>
                      <span className="muted small">{m.in_place} of {m.controls} in place</span>
                    </span>
                  </span>
                  <b className="up-t num" title={m.points ? undefined : 'Once the cap is lifted'}>
                    {m.points ? `+${m.points}` : `+${m.after_cap ?? 0} after the cap`}
                  </b>
                </summary>
                <p className="muted small">{m.blurb}</p>
                <ol className="actions">
                  {m.next.steps.map((x) => (
                    <li key={x.control}>
                      <span className="grow">
                        {x.name} <span className="mono small muted">{x.control}</span>
                        {x.lifts_cap && <span className="badge">Lifts the cap</span>}
                      </span>
                      <span className="num muted">+{x.points}</span>
                    </li>
                  ))}
                </ol>
                <p className="small mission-next">
                  {m.next.steps.length === 1 ? 'This step' : `These ${m.next.steps.length} steps together`}: <b className="up-t">+{m.next.points}</b>
                  {m.points > m.next.points && <span className="muted"> · the whole mission +{m.points}</span>}
                  {!m.points && <span className="muted"> · a failed kill switch caps the score, so these count once it is fixed</span>}
                </p>
              </details>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">Nothing left that would add points.</p>
      )}
      {done.length > 0 && (
        <p className="muted small mt">
          <ShieldCheck size={14} /> Complete: {done.map((m) => m.name).join(', ')}
        </p>
      )}
    </section>
  ); // prettier-ignore
}

function Resilience({ r }: { r: CyberScoreResult }) {
  return (
    <section className="card pad">
      <div className="card-title">Loss scenario resilience</div>
      <ol className="band-bars">
        {(Object.keys(THREATS) as Threat[]).map((t) => (
          <li key={t}>
            <span className="band-name">{THREATS[t].name}</span>
            <span className="meter">
              <span className={`fill ${r.resilience[t] < 56 ? 'bad' : r.resilience[t] < 65 ? 'warn' : 'ok'}`} style={{ width: `${r.resilience[t]}%` }} />
            </span>
            <b>{r.resilience[t]}</b>
          </li>
        ))}
      </ol>
    </section>
  ); // prettier-ignore
}

function History({ id, history }: { id: string; history: StoredScoreRow[] }) {
  const latest = history[0] ?? null;
  const move = useScoreChange(id, history.length > 1 ? latest!.seq : null);
  const parts = move.data?.change?.parts.slice(0, 5) ?? [];
  return (
    <section className="card pad">
      <div className="card-title">What moved it</div>
      {history.length < 2 ? (
        <p className="muted">One score so far. Changes show here once answers move it.</p>
      ) : move.isPending ? (
        <p className="muted">Loading…</p>
      ) : !move.data?.change ? (
        <p className="muted">The score before was stored without an explanation.</p>
      ) : (
        <>
          <p className="small">
            {move.data.from!.score} → {move.data.to.score} ({signed(move.data.change.delta)}) on {when(move.data.to.created_at)}
          </p>
          {!parts.length && <p className="muted small">The answers changed, but not the points.</p>}
          <ul className="parts">
            {parts.map((p, i) => (
              <li key={i}>
                <span className="grow">{changePartLabel(p)}</span>
                <b className={p.points < 0 ? 'down-t' : 'up-t'}>{signed(p.points)}</b>
              </li>
            ))}
          </ul>
        </>
      )}
      {history.length > 0 && (
        <>
          <div className="eyebrow mt">Stored scores</div>
          <ol className="history">
            {history.slice(0, 8).map((h) => (
              <li key={h.seq}>
                <span className="muted small">{when(h.created_at)}</span>
                <span className="grow" />
                {h.kill_switch_cap !== null && <span className="badge">cap {h.kill_switch_cap}</span>}
                <b className="num">{h.score}</b>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  ); // prettier-ignore
}
